import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SmallInputMenuComponent } from '../../../shared/components/small-input-menu/small-input-menu.component';
import { CallCenterStatsService } from '../../../services/call-center-stats.service';
import { CallCenterStatsData as ApiCallCenterStatsData, UpdateNotesRequest } from '../../../interfaces/call-center-stats.interface';
import { AuthService } from '../../../services/auth.service';
import { SummaryNotesService } from '../../../services/summary-notes.service';
import { 
  CallCenterStatsTableData as CallCenterStatsData, 
  StatsDataTemplate, 
  SummaryData 
} from '../../../interfaces';

// Konstansok
const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
type DayOfWeek = typeof DAYS_OF_WEEK[number];

const BACKGROUND_COLORS = {
  MAIN_HEADER: '#e8f4fd',
  SUB_LEVEL_1: '#f0f8f0',
  SUB_LEVEL_2: '#f5f8f5',
  SUB_LEVEL_3: '#fafbfa',
  CALLBACK: '#fff2cc',
  REJECTED: '#ffebee',
  ROBINSON: '#f3e5f5',
  NO_ANSWER: '#fce4ec',
  PROPERTY_INSURANCE: '#e1f5fe',
  WHITE: 'white'
} as const;

@Component({
  selector: 'app-call-center-stats-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatCardModule,
    MatIconModule,
    MatMenuModule,
    MatChipsModule,
    SmallInputMenuComponent
  ],
  templateUrl: './call-center-stats-table.component.html',
  styleUrls: ['./call-center-stats-table.component.scss']
})
export class CallCenterStatsTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() weekNumber: number = 1;
  @Input() weekData: any = null;
  @Input() staffMember: any = null;
  @Input() isSummaryTable: boolean = false;
  @Input() allStaffMembers: any[] = [];
  @Input() preloadedStats: ApiCallCenterStatsData | null = null; // Előre betöltött heti adat (bulk endpoint-ból)
  @Input() allPreloadedStats: ApiCallCenterStatsData[] | null = null; // Összegőző: összes user előre betöltött adata

  // Inflight kérések megszakításához
  private readonly cancelRequest$ = new Subject<void>();

  // UI állapot
  hoveredColumn: string | null = null;
  currentMenuTrigger: any = null;
  selectedColumn: string = '';
  isMenuOpen: boolean = false;
  isLoading: boolean = false;

  // Adatok
  apiStatsData: ApiCallCenterStatsData | null = null;
  private _statsData: CallCenterStatsData[] = [];
  private _lastRow: CallCenterStatsData | null = null;

  get statsData(): CallCenterStatsData[] {
    return this._statsData;
  }

  set statsData(value: CallCenterStatsData[]) {
    this._statsData = value;
    this._lastRow = value.length > 0 ? value[value.length - 1] : null;
  }
  notes: Record<DayOfWeek, string> = {
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: ''
  };

  // Összesítő táblázat notesai (summary_notes táblából)
  summaryNotes: Record<DayOfWeek, string> = {
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: ''
  };

  // Összesítő note menük
  @ViewChild('mondaySummaryNoteMenu') mondaySummaryNoteMenu!: SmallInputMenuComponent;
  @ViewChild('tuesdaySummaryNoteMenu') tuesdaySummaryNoteMenu!: SmallInputMenuComponent;
  @ViewChild('wednesdaySummaryNoteMenu') wednesdaySummaryNoteMenu!: SmallInputMenuComponent;
  @ViewChild('thursdaySummaryNoteMenu') thursdaySummaryNoteMenu!: SmallInputMenuComponent;
  @ViewChild('fridaySummaryNoteMenu') fridaySummaryNoteMenu!: SmallInputMenuComponent;

  displayedColumns: string[] = [
    'category',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'total'
  ];

  // Statikus sablon az adatok struktúrájához
  private readonly STATS_TEMPLATE: StatsDataTemplate[] = [
    { category: 'Indított hívás /Terv/', backgroundColor: BACKGROUND_COLORS.MAIN_HEADER, indentLevel: 0 },
    { category: 'Indított hívás /Tény/', backgroundColor: BACKGROUND_COLORS.MAIN_HEADER, indentLevel: 0 },
    { category: 'ebből felvette', backgroundColor: BACKGROUND_COLORS.SUB_LEVEL_1, indentLevel: 1, isSubcategory: true },
    { category: 'ebből a keresett személyt elérte', backgroundColor: BACKGROUND_COLORS.SUB_LEVEL_2, indentLevel: 1, isSubcategory: true },
    { category: 'ebből végig hallgatta', backgroundColor: BACKGROUND_COLORS.SUB_LEVEL_3, indentLevel: 1, isSubcategory: true },
    { category: 'Visszahívni', backgroundColor: BACKGROUND_COLORS.CALLBACK, indentLevel: 1, isSubcategory: true },
    { category: 'Elutasított', backgroundColor: BACKGROUND_COLORS.REJECTED, indentLevel: 1, isSubcategory: true },
    { category: 'Robinson listás', backgroundColor: BACKGROUND_COLORS.ROBINSON, indentLevel: 1, isSubcategory: true },
    { category: 'Nem vette fel', backgroundColor: BACKGROUND_COLORS.NO_ANSWER, indentLevel: 1, isSubcategory: true },
    { category: 'Egyeztetett időpont /Terv/', backgroundColor: BACKGROUND_COLORS.CALLBACK, indentLevel: 2, isSubcategory: true },
    { category: 'Egyeztetett időpont /Tény/', backgroundColor: BACKGROUND_COLORS.CALLBACK, indentLevel: 2, isSubcategory: true, isHighlighted: true },
    { category: 'Vagyonra egyeztetett időpont /Terv/', backgroundColor: BACKGROUND_COLORS.PROPERTY_INSURANCE, indentLevel: 3, isSubcategory: true },
    { category: 'Vagyonra egyeztetett időpont /Tény/', backgroundColor: BACKGROUND_COLORS.PROPERTY_INSURANCE, indentLevel: 3, isSubcategory: true },
    { category: '', backgroundColor: BACKGROUND_COLORS.WHITE, indentLevel: 0 } // Üres sor jegyzetek számára
  ];

  constructor(
    private callCenterStatsService: CallCenterStatsService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private summaryNotesService: SummaryNotesService
  ) {}

  // #region Utility Methods

  private isDayOfWeek(column: string): column is DayOfWeek {
    return DAYS_OF_WEEK.includes(column as DayOfWeek);
  }

  getRowClass(row: CallCenterStatsData): string {
    const classes: string[] = [];
    
    if (row.isSubcategory) {
      classes.push('subcategory-row');
    }
    
    if (row.isHighlighted) {
      classes.push('highlighted-row');
    }
    
    if (row.category === 'Indított hívás /Terv/' || row.category === 'Indított hívás /Tény/') {
      classes.push('header-row');
    }
    
    if (row.indentLevel && row.indentLevel > 0) {
      classes.push(`indent-level-${row.indentLevel}`);
    }
    
    return classes.join(' ');
  }

  getCellClass(value: number, columnIndex: number): string {
    if (value === 0 && columnIndex > 0) {
      return 'zero-value';
    }
    return '';
  }

  getCellClassWithColumnNote(value: number, columnIndex: number, column: string): string {
    let baseClass = this.getCellClass(value, columnIndex);

    const hasNote = this.isSummaryTable
      ? this.hasSummaryNoteForColumn(column)
      : this.hasNoteForColumn(column);
    const color = this.isSummaryTable
      ? this.getSummaryNoteColor(column)
      : this.getNoteColor(column);

    if (hasNote) {
      const colorClass = color === 'red' ? 'column-with-note-red' : 'column-with-note';
      baseClass = baseClass ? baseClass + ' ' + colorClass : colorClass;
    }

    return baseClass;
  }

  getCellStyle(row: CallCenterStatsData, column: string): any {
    const hasNote = this.isSummaryTable
      ? this.hasSummaryNoteForColumn(column)
      : this.hasNoteForColumn(column);
    const color = this.isSummaryTable
      ? this.getSummaryNoteColor(column)
      : this.getNoteColor(column);

    if (hasNote) {
      const bgColor = color === 'red' ? 'rgba(229, 57, 53, 0.35)' : '#fffbf0';
      return { 'background-color': bgColor };
    }
    return { 'background-color': row.backgroundColor };
  }

  /** A tárolt note szövege a szín-prefix nélkül (megjelenítéshez) */
  getNoteText(column: string): string {
    if (!this.isDayOfWeek(column)) return '';
    const raw = this.notes[column] || '';
    return raw.startsWith('red:') ? raw.substring(4) : raw;
  }

  /** A tárolt note színe (prefix alapján) */
  getNoteColor(column: string): 'orange' | 'red' {
    if (!this.isDayOfWeek(column)) return 'orange';
    return (this.notes[column] || '').startsWith('red:') ? 'red' : 'orange';
  }

  /** CSS osztály a note chiphez (szín alapján) */
  getNoteChipClass(column: string): string {
    return this.getNoteColor(column) === 'red' ? 'note-chip-red' : '';
  }

  isLastRow(row: CallCenterStatsData): boolean {
    return row === this._lastRow;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private calculateTotal(values: number[]): number {
    return values.reduce((sum, val) => sum + (val || 0), 0);
  }

  // #endregion

  // #region UI Event Handlers

  onColumnHover(column: string): void {
    if (!this.isMenuOpen) {
      this.hoveredColumn = column;
    }
  }

  onColumnLeave(): void {
    if (!this.isMenuOpen) {
      this.hoveredColumn = null;
    }
  }

  setSelectedColumn(column: string, trigger: any) {
    this.selectedColumn = column;
    this.currentMenuTrigger = trigger;
    this.isMenuOpen = true;
    trigger.openMenu();
  }

  onMenuAction() {
    if (this.currentMenuTrigger) {
      this.currentMenuTrigger.closeMenu();
    }
    this.isMenuOpen = false;
    this.hoveredColumn = null;
  }

  // #endregion

  // #region Note Management

  shouldShowNoteIcon(row: CallCenterStatsData, column: string): boolean {
    return this.isLastRow(row) && this.hoveredColumn === column && !this.hasNoteForColumn(column);
  }

  shouldShowNoteChip(row: CallCenterStatsData, column: string): boolean {
    return this.isLastRow(row) && this.hasNoteForColumn(column);
  }

  hasNoteForColumn(column: string): boolean {
    return this.isDayOfWeek(column) && !!this.notes[column];
  }

  onNoteAdded(note: string, column: string): void {
    if (!this.isDayOfWeek(column)) return;
    
    this.notes[column] = note;
    this.onMenuAction();
    this.saveNotesToDatabase();
  }

  onNoteCancelled(): void {
    this.onMenuAction();
  }

  onNoteDeleted(column: string): void {
    if (!this.isDayOfWeek(column)) return;
    
    this.notes[column] = '';
    this.onMenuAction();
    this.saveNotesToDatabase();
  }

  editNoteChip(column: string): void {
    this.selectedColumn = column;
  }

  // #endregion

  // #region Summary Note Management

  /** Szerkesztheti-e az összesítő notesait a jelenlegi felhasználó */
  canEditSummaryNotes(): boolean {
    return this.authService.isAdmin() || this.authService.isManager();
  }

  /** Hét napjának neve -> naptári dátum konvertálása (weekData.startDate = hétfő) */
  private getDateForColumn(column: DayOfWeek): Date {
    if (!this.weekData?.startDate) return new Date();
    const offset = DAYS_OF_WEEK.indexOf(column);
    const date = new Date(this.weekData.startDate);
    date.setDate(date.getDate() + offset);
    return date;
  }

  /** summary_notes kulcsok kinyerése egy naphoz */
  private getSummaryNoteKey(column: DayOfWeek): { year: number; month: number; dayIndex: number } {
    const date = this.getDateForColumn(column);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,       // 1-alapú, ahogy a statistics-table is használja
      dayIndex: date.getDate() - 1       // 0-alapú, ahogy a statistics-table is tárolja
    };
  }

  /** Összesítő táblázat notesainak betöltése az adatbázisból */
  private loadSummaryNotes(): void {
    if (!this.weekData?.startDate) return;

    // Egy hét esetleg 2 hónapba is nyúlhat → minden érintett év/hónap párhoz külön lekérés
    const keysToLoad = new Set<string>();
    DAYS_OF_WEEK.forEach(day => {
      const { year, month } = this.getSummaryNoteKey(day);
      keysToLoad.add(`${year}-${month}`);
    });

    const requests = Array.from(keysToLoad).map(key => {
      const [year, month] = key.split('-').map(Number);
      return this.summaryNotesService.getNotes(year, month, false);
    });

    const newSummaryNotes: Record<DayOfWeek, string> = {
      monday: '', tuesday: '', wednesday: '', thursday: '', friday: ''
    };

    forkJoin(requests).subscribe({
      next: (results) => {
        results.forEach(notes => {
          notes.forEach(note => {
            DAYS_OF_WEEK.forEach(day => {
              const { year, month, dayIndex } = this.getSummaryNoteKey(day);
              if (note.year === year && note.month === month && note.day_index === dayIndex) {
                newSummaryNotes[day] = note.note;
              }
            });
          });
        });
        this.summaryNotes = newSummaryNotes;
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  /** Van-e összesítő note az adott naphoz */
  hasSummaryNoteForColumn(column: string): boolean {
    if (!this.isDayOfWeek(column)) return false;
    return !!(this.summaryNotes[column] && this.summaryNotes[column].trim() !== '');
  }

  /** Összesítő note szövege prefix nélkül (megjelenítéshez) */
  getSummaryNoteText(column: string): string {
    if (!this.isDayOfWeek(column)) return '';
    const raw = this.summaryNotes[column] || '';
    return raw.startsWith('red:') ? raw.substring(4) : raw;
  }

  /** Összesítő note színe */
  getSummaryNoteColor(column: string): 'orange' | 'red' {
    if (!this.isDayOfWeek(column)) return 'orange';
    return (this.summaryNotes[column] || '').startsWith('red:') ? 'red' : 'orange';
  }

  /** CSS osztály az összesítő note chiphez */
  getSummaryNoteChipClass(column: string): string {
    return this.getSummaryNoteColor(column) === 'red' ? 'note-chip-red' : '';
  }

  /** Szerkesztési ikon megjelenítése az összesítő táblázathoz */
  shouldShowSummaryNoteIcon(row: CallCenterStatsData, column: string): boolean {
    return this.isLastRow(row)
      && this.hoveredColumn === column
      && !this.hasSummaryNoteForColumn(column)
      && this.canEditSummaryNotes();
  }

  /** Chip megjelenítése az összesítő táblázathoz */
  shouldShowSummaryNoteChip(row: CallCenterStatsData, column: string): boolean {
    return this.isLastRow(row) && this.hasSummaryNoteForColumn(column);
  }

  /** Összesítő note mentése */
  onSummaryNoteAdded(note: string, column: string): void {
    if (!this.isDayOfWeek(column)) return;
    const { year, month, dayIndex } = this.getSummaryNoteKey(column);
    this.summaryNotesService.upsertNote({
      year, month, day_index: dayIndex, is_kum_mode: false, note
    }).subscribe({
      next: (saved) => {
        this.summaryNotes = { ...this.summaryNotes, [column]: saved.note };
        this.cdr.detectChanges();
        this.showSuccessMessage('✅ Összesítő jegyzet sikeresen mentve!');
      },
      error: () => this.showErrorMessage('❌ Hiba az összesítő jegyzet mentésekor!')
    });
    this.onMenuAction();
  }

  /** Összesítő note törlése */
  onSummaryNoteDeleted(column: string): void {
    if (!this.isDayOfWeek(column)) return;
    const { year, month, dayIndex } = this.getSummaryNoteKey(column);
    this.summaryNotesService.deleteNote(year, month, dayIndex, false).subscribe({
      next: () => {
        this.summaryNotes = { ...this.summaryNotes, [column]: '' };
        this.cdr.detectChanges();
        this.showSuccessMessage('✅ Összesítő jegyzet sikeresen törölve!');
      },
      error: () => this.showErrorMessage('❌ Hiba az összesítő jegyzet törlése során!')
    });
    this.onMenuAction();
  }

  // #endregion
  // #region Database Operations

  /**
   * Jegyzetek mentése az adatbázisba
   */
  private saveNotesToDatabase(): void {
    // Meghatározzuk hogy melyik user jegyzeteit mentjük
    const targetUserId = this.staffMember?.id || this.authService.getCurrentUser()?.id;
    if (!targetUserId) return;

    const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
    
    // Hét nap jegyzeteit array-ba konvertáljuk (monday=0, tuesday=1, ..., friday=4)
    const notesArray: (string | null)[] = DAYS_OF_WEEK.map(day => this.notes[day] || null);

    const updateRequest: UpdateNotesRequest = {
      user_id: targetUserId,
      week_number: weekInfo.weekNumber,
      year: weekInfo.year,
      notes: notesArray
    };

    this.callCenterStatsService.updateNotes(updateRequest).subscribe({
      next: (response: ApiCallCenterStatsData) => {
        this.showSuccessMessage('Jegyzetek sikeresen mentve!');
      },
      error: (error: any) => {
        this.showErrorMessage('Hiba történt a jegyzetek mentése során!');
      }
    });
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Bezár', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Bezár', {
      duration: 5000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  // #endregion

  // #region Lifecycle Methods

  ngOnInit(): void {
    this.loadStatsData();
    if (this.isSummaryTable) {
      this.loadSummaryNotes();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preloadedStats'] || changes['allPreloadedStats'] ||
        changes['staffMember'] || changes['weekNumber'] || changes['weekData'] ||
        changes['isSummaryTable'] || changes['allStaffMembers']) {
      this.cancelRequest$.next(); // előző inflight kérések megszakítása
      this.loadStatsData();
    }
    // Összesítő notes újratöltése hétváltáskor
    if (this.isSummaryTable && (changes['weekData'] || changes['isSummaryTable'])) {
      this.loadSummaryNotes();
    }
  }

  ngOnDestroy(): void {
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
  }

  // #endregion

  // #region Data Loading

  private loadStatsData(): void {
    if (this.isSummaryTable) {
      // Összegőző tábla: előre betöltött adatok használata ha elérhető
      if (this.allPreloadedStats && this.allPreloadedStats.length > 0) {
        this.calculateSummaryFromApiData(this.allPreloadedStats);
        this.isLoading = false;
        this.cdr.detectChanges();
      } else {
        this.calculateSummaryData();
      }
    } else if (this.preloadedStats) {
      // Egyéni tábla: előre betöltött adat használata
      this.apiStatsData = this.preloadedStats;
      this.updateStatsDataFromApi();
      this.isLoading = false;
      this.cdr.detectChanges();
    } else if (this.staffMember?.id) {
      this.loadUserStats();
    } else {
      this.updateStatsData();
    }
  }

  private loadUserStats(): void {
    this.isLoading = true;
    
    const userId = this.staffMember.id;
    const observable = this.weekData?.startDate && this.weekData?.endDate
      ? this.callCenterStatsService.getUserWeekStatsByDate(userId, this.weekData.startDate, this.weekData.endDate)
      : this.getUserWeekStatsDefault(userId);

    observable.pipe(takeUntil(this.cancelRequest$)).subscribe({
      next: (data) => {
        this.apiStatsData = data;
        this.updateStatsDataFromApi();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba az adatok betöltése során:', error);
        this.updateStatsData();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getUserWeekStatsDefault(userId: number) {
    const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
    return this.callCenterStatsService.getUserWeekStats(
      userId,
      weekInfo.weekNumber || this.weekNumber,
      weekInfo.year || new Date().getFullYear()
    );
  }

  // #endregion

  // #region Data Processing

  private updateStatsDataFromApi(): void {
    if (!this.apiStatsData) {
      this.updateStatsData();
      return;
    }

    const data = this.apiStatsData;
    this.statsData = this.buildStatsDataFromApi(data);

    // Jegyzetek betöltése az API-ból - mindig
    if (data.notes && Array.isArray(data.notes)) {
      DAYS_OF_WEEK.forEach((day, index) => {
        const note = data.notes![index];
        this.notes[day] = note || '';
      });
    }
  }

  private buildStatsDataFromApi(data: ApiCallCenterStatsData): CallCenterStatsData[] {
    const apiFields: (keyof ApiCallCenterStatsData)[] = [
      'initiated_calls_plan',
      'initiated_calls_actual',
      'calls_picked_up',
      'target_person_reached',
      'listened_completely',
      'callback',
      'rejected',
      'robinson_list',
      'no_answer',
      'appointment_scheduled_plan',
      'appointment_scheduled_actual',
      'property_insurance_appointment_plan',
      'property_insurance_appointment_actual'
    ];

    return this.STATS_TEMPLATE.map((template, index) => {
      if (template.category === '') {
        // Üres sor
        return this.createEmptyStatsRow();
      }

      const apiField = apiFields[index];
      const values = apiField ? (data[apiField] as number[] || [0, 0, 0, 0, 0]) : [0, 0, 0, 0, 0];

      return {
        ...template,
        alexandra: 0,
        monday: values[0] || 0,
        tuesday: values[1] || 0,
        wednesday: values[2] || 0,
        thursday: values[3] || 0,
        friday: values[4] || 0,
        total: this.calculateTotal(values)
      };
    });
  }

  private createEmptyStatsRow(): CallCenterStatsData {
    return {
      category: '',
      alexandra: 0,
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      total: 0,
      backgroundColor: BACKGROUND_COLORS.WHITE
    };
  }

  private updateStatsData(): void {
    if (this.isSummaryTable && this.allStaffMembers.length > 0) {
      this.calculateSummaryData();
    } else {
      this.statsData = this.getBaseStatsStructure();
    }
  }

  // #endregion

  // #region Summary Data Calculation

  private calculateSummaryData(): void {
    if (!this.allStaffMembers?.length) {
      this.statsData = this.getBaseStatsStructure();
      return;
    }

    this.isLoading = true;

    const validMembers = this.allStaffMembers.filter(member => member.id);
    if (!validMembers.length) {
      this.statsData = this.getBaseStatsStructure();
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const apiCalls = validMembers.map(member => {
      const userId = member.id;
      return this.weekData?.startDate && this.weekData?.endDate
        ? this.callCenterStatsService.getUserWeekStatsByDate(userId, this.weekData.startDate, this.weekData.endDate)
        : this.getUserWeekStatsDefault(userId);
    });

    forkJoin(apiCalls).pipe(takeUntil(this.cancelRequest$)).subscribe({
      next: (allStatsData) => {
        this.calculateSummaryFromApiData(allStatsData);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba az összesítő adatok betöltése során:', error);
        this.statsData = this.getBaseStatsStructure();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private calculateSummaryFromApiData(allStatsData: ApiCallCenterStatsData[]): void {
    const summaryData = this.initializeSummaryData();

    // Összegezzük az összes munkatárs adatait
    allStatsData.forEach(data => {
      if (data) {
        this.addDataToSummary(data, summaryData);
      }
    });

    this.statsData = this.buildStatsFromSummary(summaryData);
  }

  private initializeSummaryData(): SummaryData {
    return {
      initiated_calls_actual: [0, 0, 0, 0, 0],
      initiated_calls_plan: [0, 0, 0, 0, 0],
      calls_picked_up: [0, 0, 0, 0, 0],
      target_person_reached: [0, 0, 0, 0, 0],
      listened_completely: [0, 0, 0, 0, 0],
      callback: [0, 0, 0, 0, 0],
      rejected: [0, 0, 0, 0, 0],
      robinson_list: [0, 0, 0, 0, 0],
      no_answer: [0, 0, 0, 0, 0],
      appointment_scheduled_actual: [0, 0, 0, 0, 0],
      appointment_scheduled_plan: [0, 0, 0, 0, 0],
      property_insurance_appointment_actual: [0, 0, 0, 0, 0],
      property_insurance_appointment_plan: [0, 0, 0, 0, 0]
    };
  }

  private addDataToSummary(data: ApiCallCenterStatsData, summaryData: SummaryData): void {
    for (let day = 0; day < 5; day++) {
      summaryData.initiated_calls_actual[day] += data.initiated_calls_actual[day] || 0;
      summaryData.initiated_calls_plan[day] += data.initiated_calls_plan[day] || 0;
      summaryData.calls_picked_up[day] += data.calls_picked_up[day] || 0;
      summaryData.target_person_reached[day] += data.target_person_reached[day] || 0;
      summaryData.listened_completely[day] += data.listened_completely[day] || 0;
      summaryData.callback[day] += data.callback[day] || 0;
      summaryData.rejected[day] += data.rejected[day] || 0;
      summaryData.robinson_list[day] += data.robinson_list[day] || 0;
      summaryData.no_answer[day] += data.no_answer[day] || 0;
      summaryData.appointment_scheduled_actual[day] += data.appointment_scheduled_actual[day] || 0;
      summaryData.appointment_scheduled_plan[day] += data.appointment_scheduled_plan[day] || 0;
      summaryData.property_insurance_appointment_actual[day] += data.property_insurance_appointment_actual[day] || 0;
      summaryData.property_insurance_appointment_plan[day] += data.property_insurance_appointment_plan[day] || 0;
    }
  }

  private buildStatsFromSummary(summaryData: SummaryData): CallCenterStatsData[] {
    const dataFields: (keyof SummaryData)[] = [
      'initiated_calls_plan',
      'initiated_calls_actual',
      'calls_picked_up', 
      'target_person_reached',
      'listened_completely',
      'callback',
      'rejected',
      'robinson_list',
      'no_answer',
      'appointment_scheduled_plan',
      'appointment_scheduled_actual',
      'property_insurance_appointment_plan',
      'property_insurance_appointment_actual'
    ];

    return this.STATS_TEMPLATE.map((template, index) => {
      if (template.category === '') {
        return this.createEmptyStatsRow();
      }

      const dataField = dataFields[index];
      const values = dataField ? summaryData[dataField] : [0, 0, 0, 0, 0];

      return {
        ...template,
        alexandra: 0,
        monday: values[0],
        tuesday: values[1],
        wednesday: values[2],
        thursday: values[3],
        friday: values[4],
        total: this.calculateTotal(values)
      };
    });
  }

  private getBaseStatsStructure(): CallCenterStatsData[] {
    return this.STATS_TEMPLATE.map(template => ({
      ...template,
      alexandra: 0,
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      total: 0
    }));
  }

  // #endregion


}
